import {
  bookCreationProfileForTemplate,
  bookVoucherStartsEdition,
  getTemplate,
} from "@lifescapsule/contracts";

// Each public page's title and description: set in the browser as the page opens, and written
// into its prerendered HTML for search results and link previews (scripts/prerender.mjs).

export type RouteMetadataEntry = { title: string; description: string };

export const defaultDescription =
  "Gather your family's voices, photographs, recipes, and memories into a private, family-reviewed book.";

const unitByProductionFamily = {
  NARRATIVE: "chapter",
  PICTURE_BOOK: "spread",
  RECIPE: "recipe",
  COMIC_CORE: "page",
} as const;

/**
 * An edition's own page. An edition a book voucher can start says so; any other shows its example
 * while it is being prepared, as the catalog does.
 */
export function productRouteMetadata(
  templateId: string,
  resource: "books" | "checkout",
): RouteMetadataEntry | null {
  const template = getTemplate(templateId);
  const profile = bookCreationProfileForTemplate(templateId);
  if (!template || !profile) return null;

  const unit = unitByProductionFamily[profile.productionFamily];
  if (resource === "checkout") {
    return {
      title: `Printed ordering for ${template.name} — Life's Capsule`,
      description: `Learn when printed ordering for ${template.name} will open and continue exploring the edition without placing an order.`,
    };
  }

  if (bookVoucherStartsEdition(templateId))
    return {
      title: `${template.name} — Life's Capsule`,
      description: `${template.tagline} Explore a fictional sample ${unit}, then begin your private book with a book voucher.`,
    };
  return {
    title: `${template.name} — coming soon to Life's Capsule`,
    description: `${template.tagline} Explore a fictional ${unit} example while this edition is being prepared.`,
  };
}

export const routeMetadata: Record<string, RouteMetadataEntry> = {
  "/": {
    title: "Life's Capsule — A book only your family can make",
    description: defaultDescription,
  },
  "/books": {
    title: "The books — Life's Capsule",
    description:
      "Explore 12 guided family-book formats and see which editions are available, guided, or coming soon.",
  },
  "/demo": {
    title: "Guided product demo — Life's Capsule",
    description: "Explore fictional memoir, Bedtime Story and family-book examples.",
  },
  "/demo/memoir": {
    title: "Preserve a life — Life's Capsule demo",
    description:
      "Try a fictional memory, answer a few questions and see how it becomes a book chapter.",
  },
  // The old address opens the Bedtime sample, so a link to it previews the sample.
  "/demo/bedtime": {
    title: "Bedtime Book product demo — Life's Capsule",
    description:
      "Read a finished bedtime story and see how its words and pictures became a complete book.",
  },
  "/demo/family": {
    title: "Build together — Life's Capsule demo",
    description:
      "Explore a fictional family book and its review steps. Inviting family to help isn’t available yet.",
  },
  "/demo/phone-capture": {
    title: "Phone capture safety proof — Life's Capsule demo",
    description:
      "Explore deterministic consent, retention, and provider-unavailable phone and SMS capture gates without placing calls or sending messages.",
  },
  "/demo/prompt-campaigns": {
    title: "Story prompt planning — Life's Capsule",
    description: "Learn how thoughtful prompts can help a family gather stories over time.",
  },
  "/how-it-works": {
    title: "How it works — Life's Capsule",
    description:
      "See how available editions move from thoughtful prompts through family review and a finished digital book.",
  },
  "/bedtime-story-demo": {
    title: "Bedtime Book product demo — Life's Capsule",
    description:
      "Read a finished bedtime story and see how its words and pictures became a complete book.",
  },
  "/pricing": {
    title: "Pricing — Life's Capsule",
    description:
      "See the $79.99 book voucher offer, included editions and the separate print and delivery checks. Vouchers cannot be purchased on this site.",
  },
  "/faq": {
    title: "Questions — Life's Capsule",
    description: "Answers about recording, assisted writing, privacy, printing, gifts, and more.",
  },
  "/sign-in": {
    title: "Sign in — Life's Capsule",
    description: "Sign in to your private books with an email button or code.",
  },
  "/sign-in/complete": {
    title: "Complete sign-in — Life's Capsule",
    description: "Confirm a private Life's Capsule email-button sign-in.",
  },
  "/app": {
    title: "My Library — Life's Capsule",
    description:
      "Return to your living family books, the next meaningful memory, and recent family momentum.",
  },
  "/support": {
    title: "Contact support — Life's Capsule",
    description:
      "Send a private, traceable support request from your authenticated Life's Capsule account.",
  },
  "/app/new": {
    title: "Begin a private book — Life's Capsule",
    description: "Choose your book, then start it with your Life's Capsule voucher.",
  },
  "/app/photos": {
    title: "My photos — Life's Capsule",
    description: "Find, organize, and reuse photos from your private Life's Capsule library.",
  },
  "/invitations/accept": {
    title: "Accept an invitation — Life's Capsule",
    description: "Review an existing private-book invitation with the email it was sent to.",
  },
  "/support/approve": {
    title: "Review support access — Life's Capsule",
    description: "Approve or revoke a short, owner-controlled diagnostic support session.",
  },
  "/unsubscribe": {
    title: "Pause prompt reminders — Life's Capsule",
    description: "Confirm that optional Life's Capsule story-prompt emails should be paused.",
  },
  "/resume": {
    title: "Tell your first memory — Life's Capsule",
    description: "Save one memory through a private, single-purpose Life's Capsule link.",
  },
  "/redeem": {
    title: "Use your voucher — Life's Capsule",
    description: "Use your Life's Capsule voucher to start your book.",
  },
  "/checkout/success": {
    title: "Printed ordering — Life's Capsule",
    description: "Printed ordering is not open yet. Continue exploring the Life's Capsule books.",
  },
  "/app/demo": {
    title: "Sample story studio — Life's Capsule",
    description:
      "Explore the Life's Capsule writing and review experience using fictional content.",
  },
  "/app/demo/diagnostics": {
    title: "Content-free demo diagnostics — Life's Capsule",
    description: "Review funnel counts, measured latency, error stages, and provider modes.",
  },
  "/accessibility": {
    title: "Accessibility — Life's Capsule",
    description: "How Life's Capsule supports keyboard, screen reader, zoom, and voice-first use.",
  },
  "/privacy": {
    title: "Privacy — Life's Capsule",
    description: "How Life's Capsule protects family stories, recordings, and account information.",
  },
  "/terms": {
    title: "Terms — Life's Capsule",
    description: "Terms for using Life's Capsule and its private family-book experiences.",
  },
  "/refunds": {
    title: "Refund policy — Life's Capsule",
    description: "How refunds and personalized orders will be handled when ordering opens.",
  },
  "/ai-and-media-consent": {
    title: "Creative tools and media — Life's Capsule",
    description:
      "How voice, photos, assisted writing, illustrated likenesses, privacy, and deletion work across Life’s Capsule.",
  },
};
